import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useState } from 'react';
import { Box } from '@bridebook/ui';
import BridebookLogo from '@bridebook/ui/src/components/svg/bridebook-logo';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import CollaborationFlowEndAnimation from '@bridebook/web/components/auth/animations/collaboration-flow-end-animation';
import AuthCountrySelect from '@bridebook/web/components/auth/country-select/auth-country-select';
import DownloadAppQr from '@bridebook/web/components/auth/signup-container/components/download-app-qr/download-app-qr';
import LoginComponent from '@bridebook/web/components/auth/signup-container/components/login-component';
import SignupComponent from '@bridebook/web/components/auth/signup-container/components/signup-component';
import SignupMobileWelcomeComponent from '@bridebook/web/components/auth/signup-container/components/signup-mobile-welcome-component/signup-mobile-welcome-component';
import VariantLoginComponent from '@bridebook/web/components/auth/signup-container/components/variant-login-component/variant-login-component';
import VariantSignupComponent from '@bridebook/web/components/auth/signup-container/components/variant-signup-component/variant-signup-component';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { onAuthFormFieldChange } from '@bridebook/web/lib/auth/actions';
import { useSignUpHelpers } from '@bridebook/web/lib/auth/hooks/use-signup-hooks';
import { isCollaboratorInvite } from '@bridebook/web/lib/auth/selectors';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import styles from './signup-container.style';

const SignupContainer = () => {
  const market = useMarket();
  const imgPostfix = market.language;
  const isCollaboratorsInvite = useSelector(isCollaboratorInvite);
  const [collaborationAnimationVisible, setCollaborationAnimationVisible] = useState(false);
  const dispatch = useDispatch();
  const router = useRouter();
  const {
    query: { welcome },
  } = router;

  const showMobileWelcome = welcome === 'show';

  const { isSignUp } = useSignUpHelpers();
  const { isMobile } = useSelector(getScreenType);

  const ssoPositionVariant = 'control';

  const showCollaborationAnimationVisible = useCallback((show: boolean) => {
    setCollaborationAnimationVisible(show);
  }, []);

  useEffect(() => {
    dispatch(onAuthFormFieldChange({ target: { name: 'countryCode', value: market.country } }));
  }, [dispatch, market.country]);

  const showControl = ssoPositionVariant === 'control' || isCollaboratorsInvite;

  return (
    <>
      {collaborationAnimationVisible && isCollaboratorsInvite && <CollaborationFlowEndAnimation />}
      <Box style={styles.wrapper}>
        <Box style={styles.logo}>
          <Link href={UrlHelper.index}>
            <BridebookLogo color="indigoCrush" width={180} />
          </Link>
          {!showControl && isSignUp && (
            <Box style={styles.logoCountrySelector}>
              <AuthCountrySelect />
            </Box>
          )}
        </Box>
        {isMobile && showMobileWelcome ? (
          <SignupMobileWelcomeComponent />
        ) : (
          <>
            <Box style={styles.auth}>
              <Box style={styles.signupContent} data-name="signupContent">
                {showControl ? (
                  isSignUp ? (
                    <SignupComponent
                      showCollaborationAnimationVisible={
                        isCollaboratorsInvite ? showCollaborationAnimationVisible : undefined
                      }
                    />
                  ) : (
                    <LoginComponent
                      showCollaborationAnimationVisible={showCollaborationAnimationVisible}
                    />
                  )
                ) : isSignUp ? (
                  <VariantSignupComponent ssoPositionVariant={ssoPositionVariant} />
                ) : (
                  <VariantLoginComponent ssoPositionVariant={ssoPositionVariant} />
                )}
              </Box>
            </Box>
            {showControl ? (
              <Box style={styles.image(imgPostfix, false)} />
            ) : (
              <Box style={styles.imageContainer}>
                <Box style={styles.image(imgPostfix, true)} />
                <Box style={styles.qrWrapper}>
                  <DownloadAppQr />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default memo(SignupContainer);
